import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Tab, Button, Spinner, Chip } from '@heroui/react';
import { ArrowLeft, LayoutDashboard, Database, Highlighter, Ruler, PackageOpen, Calculator, Box, Scissors, Truck, Cpu, Settings } from 'lucide-react';
import { useModuleStore } from '@/stores/moduleStore';
import { MODULE_TABS, type TabKey } from '@/config/modules';
import { useProject } from '@/api/projects';
import OverviewTab from '@/components/project/OverviewTab';
import KnowledgeTab from '@/components/project/KnowledgeTab';
import AreaTable from '@/components/project/AreaTable';
import MaterialTable from '@/components/project/MaterialTable';
import { MarkupViewer } from '@/components/markup-viewer';
import EstimateTab from '@/components/project/EstimateTab';
import NestingTab from '@/components/project/NestingTab';
import TruckLoaderTab from '@/components/project/TruckLoaderTab';
import { CabinetViewer } from '@/components/viewer3d';
import ProjectSettingsTab from '@/components/project/ProjectSettingsTab';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import MobileProjectFiles from '@/components/project/mobile/MobileProjectFiles';


const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
  NEW: 'default',
  IN_PROGRESS: 'primary',
  REVIEW: 'warning',
  COMPLETED: 'success',
  ON_HOLD: 'danger',
};

const STATUS_LABELS: Record<string, string> = {
  NEW: 'New',
  IN_PROGRESS: 'In Progress',
  REVIEW: 'Review',
  COMPLETED: 'Completed',
  ON_HOLD: 'On Hold',
};

const TYPE_LABELS: Record<string, string> = {
  RESIDENTIAL: 'Residential',
  COMMERCIAL: 'Commercial',
  INDUSTRIAL: 'Industrial',
  MIXED_USE: 'Mixed Use',
  RETAIL: 'Retail',
  HEALTHCARE: 'Healthcare',
  EDUCATION: 'Education',
};

function DesktopProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [initialDrawingId, setInitialDrawingId] = useState<string | undefined>(undefined);

  const { activeModule } = useModuleStore();
  const visibleTabs = MODULE_TABS[activeModule];

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0] ?? 'overview');
    }
  }, [activeModule]);

  const { data: project, isLoading, isError } = useProject(id!);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-sm" style={{ color: 'var(--red)' }}>
          Project not found or failed to load.
        </p>
        <Button
          size="sm"
          variant="bordered"
          startContent={<ArrowLeft size={14} />}
          onPress={() => navigate('/')}
          className="border-[var(--border)] text-[var(--text-secondary)]"
        >
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full" style={{ backgroundColor: 'var(--bg-base)' }}>
      {/* ─── Page Header ─────────────────────────────────────────────────── */}
      <div
        className="border-b px-6 pt-5 pb-0"
        style={{
          borderColor: 'var(--border)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-xs transition-colors focus:outline-none focus-visible:underline"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)')
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)')
            }
          >
            <ArrowLeft size={12} />
            Dashboard
          </button>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            /
          </span>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            {project.project_name}
          </span>
        </div>

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1
                className="text-xl font-semibold truncate"
                style={{ color: 'var(--text-primary)' }}
              >
                {project.project_name}
              </h1>
              <Chip
                size="sm"
                color={STATUS_COLORS[project.status] ?? 'default'}
                variant="flat"
              >
                {STATUS_LABELS[project.status] ?? project.status}
              </Chip>
            </div>

            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {project.client?.name ?? 'Unknown Client'}
              </span>
              {project.address && (
                <>
                  <span style={{ color: 'var(--text-muted)' }}>·</span>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {project.address}
                  </span>
                </>
              )}
              <span style={{ color: 'var(--text-muted)' }}>·</span>
              <span className="text-xs font-ui" style={{ color: 'var(--text-muted)' }}>
                {TYPE_LABELS[project.project_type] ?? project.project_type}
              </span>
              {project.total_gfa != null && (
                <>
                  <span style={{ color: 'var(--text-muted)' }}>·</span>
                  <span className="text-xs font-ui" style={{ color: 'var(--accent)' }}>
                    {project.total_gfa.toLocaleString()} m²
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs — anchored to the bottom of the header */}
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(key as TabKey)}
          variant="underlined"
          size="sm"
          classNames={{
            base: '-mb-px',
            tabList: 'gap-0 p-0 bg-transparent border-b-0',
            tab: 'px-4 h-9 text-xs data-[selected=true]:text-[var(--accent)] text-[var(--text-muted)] data-[hover=true]:text-[var(--text-secondary)]',
            cursor: 'bg-[var(--accent)] h-0.5',
            panel: 'p-0',
          }}
        >
          {visibleTabs.includes('overview') && (
            <Tab
              key="overview"
              title={
                <span className="flex items-center gap-1.5">
                  <LayoutDashboard size={13} />
                  Overview
                </span>
              }
            />
          )}
          {visibleTabs.includes('knowledge') && (
            <Tab
              key="knowledge"
              title={
                <span className="flex items-center gap-1.5">
                  <Database size={13} />
                  Knowledge
                  {project.knowledge_count > 0 && (
                    <span
                      className="text-[10px] font-ui rounded-full px-1.5 py-0.5 leading-none"
                      style={{
                        backgroundColor: 'var(--bg-hover)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {project.knowledge_count}
                    </span>
                  )}
                </span>
              }
            />
          )}
          {visibleTabs.includes('markup') && (
            <Tab
              key="markup"
              title={
                <span className="flex items-center gap-1.5">
                  <Highlighter size={13} />
                  Markup Review
                  {project.markup_count > 0 && (
                    <span
                      className="text-[10px] font-ui rounded-full px-1.5 py-0.5 leading-none"
                      style={{
                        backgroundColor: 'var(--bg-hover)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {project.markup_count}
                    </span>
                  )}
                </span>
              }
            />
          )}
          {visibleTabs.includes('areas') && (
            <Tab
              key="areas"
              title={
                <span className="flex items-center gap-1.5">
                  <Ruler size={13} />
                  Areas
                  {project.area_count > 0 && (
                    <span
                      className="text-[10px] font-ui rounded-full px-1.5 py-0.5 leading-none"
                      style={{
                        backgroundColor: 'var(--bg-hover)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {project.area_count}
                    </span>
                  )}
                </span>
              }
            />
          )}
          {visibleTabs.includes('materials') && (
            <Tab
              key="materials"
              title={
                <span className="flex items-center gap-1.5">
                  <PackageOpen size={13} />
                  Materials
                  {project.material_count > 0 && (
                    <span
                      className="text-[10px] font-ui rounded-full px-1.5 py-0.5 leading-none"
                      style={{
                        backgroundColor: 'var(--bg-hover)',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {project.material_count}
                    </span>
                  )}
                </span>
              }
            />
          )}
          {visibleTabs.includes('estimate') && (
            <Tab
              key="estimate"
              title={
                <span className="flex items-center gap-1.5">
                  <Calculator size={13} />
                  Estimate
                </span>
              }
            />
          )}
          {visibleTabs.includes('nesting') && (
            <Tab
              key="nesting"
              title={
                <span className="flex items-center gap-1.5">
                  <Scissors size={13} />
                  Nesting
                </span>
              }
            />
          )}
          {visibleTabs.includes('truck-loader') && (
            <Tab
              key="truck-loader"
              title={
                <div className="flex items-center gap-1.5">
                  <Truck size={13} />
                  <span>Truck Loader</span>
                </div>
              }
            />
          )}
          {visibleTabs.includes('viewer3d') && (
            <Tab
              key="viewer3d"
              title={
                <span className="flex items-center gap-1.5">
                  <Box size={13} />
                  3D View
                </span>
              }
            />
          )}
          {visibleTabs.includes('cnc') && (
            <Tab
              key="cnc"
              title={
                <span className="flex items-center gap-1.5">
                  <Cpu size={13} />
                  CNC
                </span>
              }
            />
          )}
          {visibleTabs.includes('settings') && (
            <Tab
              key="settings"
              title={
                <span className="flex items-center gap-1.5">
                  <Settings size={13} />
                  Settings
                </span>
              }
            />
          )}
        </Tabs>
      </div>

      {/* ─── Tab Content ─────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto">
        {activeTab === 'overview' && <OverviewTab project={project} />}

        <div style={{ display: activeTab === 'knowledge' ? undefined : 'none' }}>
          <KnowledgeTab
            projectId={id!}
            onSwitchToMarkupReview={(knowledgeItemId?: string) => {
              setInitialDrawingId(knowledgeItemId);
              setActiveTab('markup');
            }}
          />
        </div>

        {activeTab === 'markup' && (
          <MarkupViewer projectId={id!} initialDrawingId={initialDrawingId} />
        )}

        {activeTab === 'areas' && <AreaTable projectId={id!} />}

        {activeTab === 'materials' && <MaterialTable projectId={id!} />}

        {activeTab === 'estimate' && <EstimateTab projectId={id!} />}

        {activeTab === 'nesting' && <NestingTab projectId={id!} />}

        {activeTab === 'truck-loader' && <TruckLoaderTab projectId={id!} projectName={project?.project_name} />}

        {activeTab === 'viewer3d' && <CabinetViewer projectId={id!} />}

        {activeTab === 'cnc' && (
          <div className="p-6 text-center" style={{ color: 'var(--text-muted)' }}>
            CNC tab — will link to CNC Queue
          </div>
        )}

        {activeTab === 'settings' && <ProjectSettingsTab projectId={id!} />}
      </div>
    </div>
  );
}

export default function ProjectDetailPage() {
  const { isMobile } = useBreakpoint();
  if (isMobile) return <MobileProjectFiles />;
  return <DesktopProjectDetailPage />;
}
